import { Box, Image, Stack, Text } from '@chakra-ui/react';

import { ProductSubscriptionDuration } from '@/client/services/api/graphql/gql/graphql';
import { useTranslation } from 'react-i18next';

interface LearnerDashboardSubscriptionListItemProps {
  title: string;
  description: string;
  price: number;
  subscriptionDuration: ProductSubscriptionDuration;
  image: string;
  actionGroup?: React.ReactNode;
  orientation?: 'horizontal' | 'vertical';
}

export default function LearnerDashboardSubscriptionListItem({
  title,
  description,
  price,
  subscriptionDuration,
  image,
  actionGroup = undefined,
  orientation = 'horizontal',
}: LearnerDashboardSubscriptionListItemProps) {
  const { t } = useTranslation();

  const subscriptionDurationMap = {
    [ProductSubscriptionDuration.Monthly]: t('learners.dashboard.subscriptions.duration.monthly'),
    [ProductSubscriptionDuration.Yearly]: t('learners.dashboard.subscriptions.duration.yearly'),
    [ProductSubscriptionDuration.Daily]: t('learners.dashboard.subscriptions.duration.daily'),
    [ProductSubscriptionDuration.Weekly]: t('learners.dashboard.subscriptions.duration.weekly'),
  };

  return (
    <Stack
      direction={{ base: 'column', md: orientation === 'horizontal' ? 'row' : 'column' }}
      spacing={{ base: 4, md: orientation === 'horizontal' ? 12 : 4 }}
      border="1px solid"
      borderColor="neutral.100"
      borderRadius="24px"
      padding={8}
    >
      <Stack spacing={4}>
        <Image
          src={image || '/public/images/cardPlaceholder.png'}
          alt={title}
          objectFit="cover"
          height="120px"
          width={{ base: 'full', md: orientation === 'horizontal' ? '260px' : 'full' }}
          borderRadius="16px"
        />

        {orientation === 'horizontal' && actionGroup}
      </Stack>

      <Stack spacing={4}>
        <Text fontSize="16px" fontWeight={600} color="baseBlack">
          {title}
        </Text>

        <Box
          backgroundColor="neutral.50"
          border="1px solid"
          borderColor="neutral.200"
          borderRadius="8px"
          paddingX={4}
          paddingY={3}
          width="fit-content"
        >
          <Text fontSize="14px" fontWeight={600} color="baseBlack">
            ${price}{' '}
            <Text as="span" fontWeight={400}>
              {' '}
              / {t('learners.dashboard.subscriptions.billed')}{' '}
              {subscriptionDurationMap[subscriptionDuration]}
            </Text>
          </Text>
        </Box>

        <Text fontSize="14px" fontWeight={400} color="baseBlack">
          {description}
        </Text>
      </Stack>

      {orientation === 'vertical' && actionGroup}
    </Stack>
  );
}
